import { createFileRoute } from '@tanstack/react-router';
import { RefreshCwIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { preLoadDefaultProject } from '@/lib/queries/project';
import { type Incident } from '@/lib/queries/rca/incidents';
import { mockDemoIncidents } from '@/lib/queries/rca/mockData';

import { EmptyIncidents, NoFilteredIncidents } from './-components/EmptyStates';
import { IncidentCard } from './-components/IncidentCard';
import { getPrimaryIncidentData } from './-components/IncidentCardHelpers';
import { IncidentFilters } from './-components/IncidentFilters';
import { sortIncidents } from './-utils/sorting';

export const Route = createFileRoute('/_root/rca-demo/')({
  component: RouteComponent,
  loader: async ({ context }) => {
    const project = await preLoadDefaultProject({ context });

    // For demo, use mock data instead of real PagerDuty incidents
    return project;
  },
});

function RouteComponent() {
  Route.useLoaderData();

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');

  // Use mock demo incidents instead of real data
  const incidents = mockDemoIncidents;

  // Filter and sort incidents
  const filteredAndSortedIncidents = useMemo(() => {
    const filtered = incidents.filter((incident: Incident) => {
      const primaryData = getPrimaryIncidentData(incident);
      if (!primaryData) {
        return false;
      }

      const statusMatch =
        statusFilter === 'all' || primaryData.status === statusFilter;
      const priorityMatch =
        severityFilter === 'all' || primaryData.priority === severityFilter;

      return statusMatch && priorityMatch;
    });

    return sortIncidents(filtered, sortBy);
  }, [incidents, statusFilter, severityFilter, sortBy]);

  const handleClearFilters = () => {
    setStatusFilter('all');
    setSeverityFilter('all');
  };

  return (
    <div className="h-full flex flex-col pb-24">
      <div className="flex flex-row justify-between items-center mb-6">
        <h1 className="text-3xl">
          <FormattedMessage id="incidents.investigations.title" />
        </h1>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <RefreshCwIcon className={`h-4 w-4 animate-spin`} />
          <FormattedMessage id="incidents.autoRefreshing" />
        </div>
      </div>

      <p className="mb-6 text-gray-600 dark:text-gray-400">
        <FormattedMessage id="incidents.description" />
      </p>

      {/* Mobile Filters - Top on small screens */}
      <div className="block lg:hidden">
        <IncidentFilters
          statusFilter={statusFilter}
          severityFilter={severityFilter}
          sortBy={sortBy}
          onStatusChange={setStatusFilter}
          onSeverityChange={setSeverityFilter}
          onSortChange={setSortBy}
          onClearFilters={handleClearFilters}
          isMobile={true}
        />
        <hr className="my-6 border-gray-200 dark:border-gray-700" />
      </div>

      {/* Main Content */}
      <div className="flex flex-1 gap-6 h-full">
        {/* Desktop Sidebar - Filters */}
        <div className="hidden lg:block">
          <IncidentFilters
            statusFilter={statusFilter}
            severityFilter={severityFilter}
            sortBy={sortBy}
            onStatusChange={setStatusFilter}
            onSeverityChange={setSeverityFilter}
            onSortChange={setSortBy}
            onClearFilters={handleClearFilters}
            isMobile={false}
          />
        </div>

        {/* Content - Incidents */}
        <div className="flex-1">
          {incidents.length === 0 ? (
            <EmptyIncidents />
          ) : filteredAndSortedIncidents.length === 0 ? (
            <NoFilteredIncidents onClearFilters={handleClearFilters} />
          ) : (
            <div className="space-y-4 h-full overflow-scroll">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <FormattedMessage
                  id="incidents.showingCount"
                  values={{
                    count: filteredAndSortedIncidents.length,
                    total: incidents.length,
                  }}
                />
              </div>
              {filteredAndSortedIncidents.map(incident => (
                <IncidentCard key={incident.id} incident={incident} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
